import { useState, useEffect, useMemo } from 'react';
import { Download, Calendar, User, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { useDate } from '@/contexts/DateContext';
import { getShopDayLabel, getTodayInShopTz } from '@/utils/shopTime';
import { useCompletedTicketEfficiency } from '@/hooks/kpi/useCompletedTicketEfficiency';
import { fetchTicketLaborMinutes } from '@/lib/labor/ticketLabor';

interface CompletedTicketsSectionProps {
  selectedDate: Date;
}

export const CompletedTicketsSection = ({ selectedDate }: CompletedTicketsSectionProps) => {
  const { toast } = useToast();
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const { selectedDayLabel } = useDate();
  const [tickets, setTickets] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    // R10-L-1: Use shop TZ for default date range
    const endDate = getTodayInShopTz(shopTimezone);
    const startDateObj = parseISO(endDate + 'T00:00:00');
    startDateObj.setDate(startDateObj.getDate() - 30);
    const startDate = format(startDateObj, 'yyyy-MM-dd');
    return {
      startDate,
      endDate,
      technicianId: 'all',
      searchCustomer: '',
    };
  });

  // ARC-A3: dep tightened from `[activeShop, filters]` (full activeShop
  // object) to `[activeShop?.id, filters]`. loadData() reads `activeShop`
  // for nullness + `activeShop.id` for the .eq filter; ID-only suffices
  // and skips re-fires triggered by activeShop reference replacement
  // (e.g., refreshActiveShop after addon toggle, ARC-A2 useMemo
  // re-emitting on unrelated state changes pre-A3).
  useEffect(() => {
    loadData();
  }, [activeShop?.id, filters]);

  const loadData = async () => {
    if (!activeShop) return;

    try {
      setLoading(true);

      // Load technicians
      const { data: techData } = await supabase
        .from('technicians')
        .select('id, name')
        .eq('shop_id', activeShop.id)
        .order('name');

      setTechnicians(techData || []);

      // Load completed tickets with explicit relationship naming
      let query = supabase
        .from('tickets')
        .select(`
          *,
          vehicle:vehicles(id, year, make, model, engine_size),
          ticket_jobs(
            job_id,
            actual_minutes,
            job_status,
            job:jobs(*)
          ),
          assigned_technician:technicians!tickets_assigned_tech_id_fkey(name),
          completed_technician:technicians!tickets_completed_by_tech_id_fkey(name)
        `)
        .eq('shop_id', activeShop.id)
        .eq('status', 'Completed')
        // CSI-S1: exclude soft-deleted Completed tickets from the dashboard list.
        .is('deleted_at', null)
        // R11-M-1: Convert shop-local day bounds to UTC for timestamptz comparison
        .gte('completed_at', fromZonedTime(`${filters.startDate}T00:00:00`, shopTimezone).toISOString())
        .lte('completed_at', fromZonedTime(`${filters.endDate}T23:59:59`, shopTimezone).toISOString())
        .order('completed_at', { ascending: false });

      if (filters.technicianId !== 'all') {
        query = query.eq('completed_by_tech_id', filters.technicianId);
      }

      if (filters.searchCustomer) {
        query = query.ilike('customer_name', `%${filters.searchCustomer}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // oct20e LABOR (FD-2026-10-24-B, row 6a): actual labor = corrected session minutes, book = approved labor —
      // never tickets.predicted_minutes (the scheduling duration) nor a sum of ticket_jobs.actual_minutes (0 when an
      // episode is finalized without explicit allocations).
      const rows = data || [];
      const labor = await fetchTicketLaborMinutes(rows.map((t: any) => t.id));
      setTickets(rows.map((t: any) => ({ ...t, labor_actual_minutes: labor.get(t.id)?.actualLaborMinutes ?? null,
        labor_approved_minutes: labor.get(t.id)?.approvedLaborMinutes ?? null })));
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateEfficiency = (ticket: any) => {
    const bookMinutes = ticket.labor_approved_minutes ?? 0;
    const actualMinutes = ticket.labor_actual_minutes ?? 0;

    if (actualMinutes === 0) return null;
    return Math.round((bookMinutes / actualMinutes) * 100);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Customer', 'Vehicle', 'Technician', 'Jobs', 'Book Time', 'Actual Time', 'Efficiency'];
    const rows = tickets.map(ticket => {
      const efficiency = calculateEfficiency(ticket);
      const bookMinutes = ticket.labor_approved_minutes ?? 0;
      const actualMinutes = ticket.labor_actual_minutes ?? 0;
      
      return [
        // R10-L-2: Format in shop TZ
        formatInTimeZone(new Date(ticket.completed_at), shopTimezone, 'yyyy-MM-dd HH:mm'),
        ticket.customer_name || 'N/A',
        `${ticket.vehicle?.year} ${ticket.vehicle?.make} ${ticket.vehicle?.model}`,
        ticket.completed_technician?.name || ticket.assigned_technician?.name || 'N/A',
        ticket.ticket_jobs?.map((tj: any) => tj.job?.name).join('; ') || '',
        `${bookMinutes}m`,
        `${actualMinutes}m`,
        efficiency ? `${efficiency}%` : 'N/A'
      ];
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `completed-tickets-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Export Complete',
      description: `Exported ${tickets.length} completed tickets`,
    });
  };

  // Filter tickets for selected date only (for summary cards)
  const selectedDateTickets = useMemo(() => {
    return tickets.filter(t => {
      if (!t.completed_at) return false;
      // R10-M-1: Compare in shop TZ, not browser TZ
      return getShopDayLabel(t.completed_at, shopTimezone) === selectedDayLabel;
    });
  }, [tickets, selectedDayLabel, shopTimezone]);

  // K-T-13-C: per-selected-day aggregate efficiency now sourced from
  // canonical `kpi_completed_ticket_efficiency` RPC. Replaces the
  // prior mean-of-per-ticket-ratios reduce over `selectedDateTickets`
  // (a client-side narrowing of `tickets` by shop-day label) with the
  // canonical SUM/SUM ratio + MIN_KPI_SAMPLE_SIZE = 3 sample gate. The
  // selected-day window is computed from the `selectedDate` prop
  // formatted in the shop's timezone, then passed to the hook as
  // start = end = that single shop-day.
  //
  // The per-ticket `calculateEfficiency` helper above stays — it's
  // still used by per-row Efficiency badges and the CSV export.
  const selectedDayStr = formatInTimeZone(selectedDate, shopTimezone, 'yyyy-MM-dd');
  const { data: aggregateEfficiency } = useCompletedTicketEfficiency({
    shopId: activeShop?.id ?? '',
    startDate: selectedDayStr,
    endDate: selectedDayStr,
    shopTimezone,
    technicianId: filters.technicianId,
    customerSearch: filters.searchCustomer,
  });
  const avgEfficiencyPercent: number | null = aggregateEfficiency?.gated
    || aggregateEfficiency?.ratio == null
    ? null
    : Math.round(aggregateEfficiency.ratio * 100);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Completed</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{selectedDateTickets.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {format(selectedDate, 'MMM d, yyyy')}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Efficiency</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {avgEfficiencyPercent !== null ? `${avgEfficiencyPercent}%` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {format(selectedDate, 'MMM d, yyyy')}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Technicians</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(selectedDateTickets.map(t => t.completed_by_tech_id).filter(Boolean)).size}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {format(selectedDate, 'MMM d, yyyy')}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Export Data</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Button onClick={exportToCSV} disabled={tickets.length === 0} size="sm" className="w-full">
              Export CSV
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter completed tickets by date, technician, or customer</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Technician</Label>
              <Select value={filters.technicianId} onValueChange={(value) => setFilters(prev => ({ ...prev, technicianId: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Technicians</SelectItem>
                  {technicians.map(tech => (
                    <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Customer Name</Label>
              <Input
                placeholder="Search customer..."
                value={filters.searchCustomer}
                onChange={(e) => setFilters(prev => ({ ...prev, searchCustomer: e.target.value }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Completed Tickets</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No completed tickets found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Technician</TableHead>
                  <TableHead>Jobs</TableHead>
                  <TableHead className="text-right">Book Time</TableHead>
                  <TableHead className="text-right">Actual Time</TableHead>
                  <TableHead className="text-right">Efficiency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map(ticket => {
                  const efficiency = calculateEfficiency(ticket);
                  const bookMinutes = ticket.labor_approved_minutes ?? 0;
                  const actualMinutes = ticket.labor_actual_minutes ?? 0;

                  return (
                    <TableRow key={ticket.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatInTimeZone(new Date(ticket.completed_at), shopTimezone, 'MM/dd/yy HH:mm')}
                      </TableCell>
                      <TableCell>{ticket.customer_name || 'N/A'}</TableCell>
                      <TableCell>
                        {ticket.vehicle ? `${ticket.vehicle.year} ${ticket.vehicle.make} ${ticket.vehicle.model}` : 'N/A'}
                      </TableCell>
                      <TableCell>{ticket.completed_technician?.name || ticket.assigned_technician?.name || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          {ticket.ticket_jobs?.map((tj: any, idx: number) => (
                            <div key={idx} className="text-xs">
                              {tj.job?.name}
                              {tj.actual_minutes && (
                                <span className="text-muted-foreground ml-1">({tj.actual_minutes}m)</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{bookMinutes}m</TableCell>
                      <TableCell className="text-right">{actualMinutes}m</TableCell>
                      <TableCell className="text-right">
                        {efficiency ? (
                          <Badge variant={efficiency >= 100 ? 'default' : 'secondary'}>
                            {efficiency}%
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
